import React, { useEffect, useState, useRef, memo, useContext } from "react";
import { Entity } from "../model/entities";
import Referent from "../UI/Referent";
import { UUID } from "../model/uuid";
import { v4 as uuidv4 } from "uuid";
import SearchBar from "../UI/SearchBar";
import matcher from "../utils/regexMatcher";
import MetaItem from "../UI/MetaItem";
import { Ontology, RoleType, Type, SumType, ProductType } from "./ontology";
import { MentionModelContext, ModalOpenContext } from "../contexts";
import { HOTKEYS, expandSVG } from "../constants";
import { Situation as SituationIntermediate } from "../structure/intermediates";
import Modal from "../UI/Modal";
import { Node, referentTypeCheck } from "../utils/expressionTree";
import { Box } from "../UI/Box";
import { ToggleButton } from "../UI/ToggleButton";
import {
	initializeRoleSetMap,
	initializeSituationExpressionTree
} from "../utils/situationOntologyScripts";
import { UntitledReferent } from "../UI/UntitledReferent";
import { MentionModel, ToastType } from "../communication";
import { EntityAnnotationScheme, SituationAnnotationScheme } from "./scheme";

export type ReferentAnnotationScheme =
	| EntityAnnotationScheme
	| SituationAnnotationScheme;
interface ReferentAnnotationProps {
	activateTypeIds: (uuid?: UUID) => void;
	annotationScheme: ReferentAnnotationScheme;
	activeStructureIds: [UUID, UUID, MentionModel][];
	activateStructureIds: (
		uuids: UUID[],
		tokenizationUUIDs: UUID[],
		mentionModel: MentionModel
	) => void;
	deactivateStructureIds: () => void;
	activeTypeIds: UUID[];
	entityList: Entity[];
	situationList: SituationIntermediate[];
	spanMap: Map<string, string>;
	addMentionToEntity: (entity: Entity, mentionId: UUID[]) => void;
	createEntity: (entity: Entity) => void;
	deleteEntity: (uuid: UUID) => void;
	mutateEntity: (
		attribute: "canonicalName" | "type",
		value: string,
		uuid: string
	) => void;
	removeMentionFromEntity: (entity: Entity, mentionId: UUID) => void;
	addMentionToSituation: (
		situation: SituationIntermediate,
		mentionId: UUID[]
	) => void;
	createSituation: (situation: SituationIntermediate) => void;
	deleteSituation: (uuid: UUID) => void;
	mutateSituation: (
		attribute: string,
		value: string,
		uuid: string,
		kind?: "entity" | "situation" | "options"
	) => void;
	removeMentionFromSituation: (
		situation: SituationIntermediate,
		mentionId: UUID
	) => void;
	setMentionModel: React.Dispatch<
		React.SetStateAction<"entity" | "situation">
	>;
	addToUserInteractions: (
		action: "create" | "delete" | "set" | "add",
		object: {
			type: "entityMention" | "situationMention" | "entity" | "situation";
			text: string;
		},
		attribute?: {
			type: string;
			value: string;
		},
		type?: string,
		role?: string,
		referent?: string
	) => void;
	displayToast: (message: string, toastType: ToastType) => void;
}

function ReferentAnnotation(props: ReferentAnnotationProps) {
	const mentionModel = useContext(MentionModelContext);
	const { modalOpen, setModalOpen } = useContext(ModalOpenContext);

	const [entityList, setEntityList] = useState<Entity[]>(props.entityList);
	const [focusedReferent, setFocusedReferent] = useState<number | undefined>(
		undefined
	);
	const [metaOntology, setMetaOntology] = useState<
		Ontology<Type> | Ontology<RoleType> | string[] | undefined
	>(_initializeMetaOntology());
	const [searchOpen, setSearchOpen] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");
	const [situationList, setSituationList] = useState<SituationIntermediate[]>(
		props.situationList
	);

	const [metaModalExpanded, setMetaModalExpanded] = useState<boolean>(false);

	const entityMap = useRef<Map<string, string>>(_initializeEntityMap());
	const situationMap = useRef<Map<string, string>>(_initializeSituationMap());

	const [untitledReferent, setUntitledReferent] = useState<boolean>(false);

	const situationExpressionTree = useRef<Map<string, Node>>(
		props.annotationScheme.subkind === "situation"
			? initializeSituationExpressionTree(
					props.annotationScheme.roleOntologyType,
					props.annotationScheme
			  )
			: new Map<string, Node>()
	);
	const roleSetMap = useRef<
		Map<string, (RoleType | SumType | ProductType)[]>
	>(
		props.annotationScheme.subkind === "situation"
			? initializeRoleSetMap(
					props.annotationScheme.roleOntologyType,
					props.annotationScheme
			  )
			: new Map<string, (RoleType | SumType | ProductType)[]>()
	);
	const [referentSituationArgumentMap, setReferentSituationArgumentMap] = useState<
		Map<string, Map<string, Set<string>>>
	>(initializeSituationArgumentMap(situationList));
	const entityToSituationRoleMap = useRef<Map<string, Set<string>>>(
		_initializeEntityToSituationRoleMap()
	);

	useEffect(() => {
		setEntityList(props.entityList);
		setSituationList(props.situationList);
		entityMap.current = _initializeEntityMap();
		situationMap.current = _initializeSituationMap();
		situationExpressionTree.current =
			props.annotationScheme.subkind === "situation"
				? initializeSituationExpressionTree(
						props.annotationScheme.roleOntologyType,
						props.annotationScheme
				  )
				: new Map<string, Node>();
		roleSetMap.current =
			props.annotationScheme.subkind === "situation"
				? initializeRoleSetMap(
						props.annotationScheme.roleOntologyType,
						props.annotationScheme
				  )
				: new Map<string, (RoleType | SumType | ProductType)[]>();
		setReferentSituationArgumentMap(initializeSituationArgumentMap(props.situationList));
		entityToSituationRoleMap.current =
			_initializeEntityToSituationRoleMap();
		setMetaOntology(_initializeMetaOntology());
	}, [props.annotationScheme]);

	// Add an event listener for creating new entity
	useEffect(() => {
		const cb = (e: KeyboardEvent) => {
			if (HOTKEYS.createReferent(e)) {
				createReferentWithMention();
			}
		};
		if (props.activeStructureIds.length) {
			document.addEventListener("keydown", cb);
		}

		return () => document.removeEventListener("keydown", cb);
	}, [props.activeStructureIds, mentionModel]);

	// Update this component's entityList each time props.entityList updates
	useEffect(() => {
		setEntityList(props.entityList);
		if (focusedReferent === undefined) {
			setFocusedReferent(0);
		}
	}, [props.entityList]);

	// Update this component's situationList each time props.situationList updates
	useEffect(() => {
		setSituationList(props.situationList);
		if (focusedReferent === undefined) {
			setFocusedReferent(0);
		}
	}, [props.situationList]);

	// CURRENT BEHAVIOUR: Update activeTypeIds in communication when states change.
	useEffect(() => {
		if (focusedReferent === undefined) {
			props.activateTypeIds();
		} else if (
			mentionModel === "entity" &&
			entityList.length > focusedReferent &&
			entityList.length
		) {
			props.activateTypeIds(entityList[focusedReferent].uuid);
		} else if (
			mentionModel === "situation" &&
			situationList.length > focusedReferent &&
			situationList.length
		) {
			if (situationList.length) {
				props.activateTypeIds(situationList[focusedReferent].uuid);
			}
		}
	}, [focusedReferent, entityList, situationList]);

	// When activeTypeIds is set to an empty array, set focusedReferent to undefined
	useEffect(() => {
		if (!props.activeTypeIds.length) {
			setFocusedReferent(undefined);
		}
	}, [props.activeTypeIds]);

	// If a span is active and no entity is focused, focus the first referent
	useEffect(() => {
		if (props.activeStructureIds.length) {
			if (!focusedReferent) {
				if (
					(mentionModel === "entity" && entityList.length) ||
					(mentionModel === "situation" && situationList.length)
				) {
					setFocusedReferent(0);
				}
			}
		}
	}, [props.activeStructureIds, mentionModel]);

	// Add/remove the event listener to open the searchbar. Listens to alphanumeric key presses or command/control + f
	// to trigger
	useEffect(() => {
		const keydownHandler = (e: KeyboardEvent) => {
			if (
				!(e.metaKey || e.ctrlKey) &&
				((e.keyCode < 58 && e.keyCode > 47) ||
					(e.keyCode > 64 && e.keyCode < 91))
			) {
				setSearchOpen(true);
				setSearchText("");
			} else if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setSearchOpen(true);
			}
		};
		if (!searchOpen && !metaModalExpanded) {
			document.addEventListener("keydown", keydownHandler);
		} else {
			document.removeEventListener("keydown", keydownHandler);
		}
		return () => document.removeEventListener("keydown", keydownHandler);
	}, [searchOpen, metaModalExpanded]);

	// If mentionModel changes, close the searchBar and reset searchText.
	// Also, set the focusedReferent in the menu to the first or undefined depending on
	// whether the referentList (entityList or situationList) is empty.
	useEffect(() => {
		if (searchOpen) {
			setSearchOpen(false);
			setSearchText("");
		}

		if (focusedReferent !== undefined) {
			if (mentionModel === "entity") {
				if (!entityList.length) {
					props.activateTypeIds();
					setFocusedReferent(undefined);
				} else {
					props.activateTypeIds(entityList[0].uuid);
					setFocusedReferent(0);
				}
			} else {
				if (!situationList.length) {
					props.activateTypeIds();
					setFocusedReferent(undefined);
				} else {
					props.activateTypeIds(situationList[0].uuid);
					setFocusedReferent(0);
				}
			}
		}
	}, [mentionModel]);

	// When searchText changes, filter the entityList
	useEffect(() => {
		if (searchOpen && searchText) {
			if (mentionModel === "entity") {
				const newList = props.entityList.filter((entity: Entity) => {
					return matcher(searchText, entity.canonicalName || "");
				});
				if (props.activeTypeIds.length) {
					const newIndex = newList.findIndex((entity: Entity) => {
						return (
							entity.uuid.uuidString ===
							props.activeTypeIds[0].uuidString
						);
					});
					setFocusedReferent(newIndex === -1 ? undefined : newIndex);
				}
				setEntityList(newList);
			} else {
				const newList = props.situationList.filter(
					(situation: SituationIntermediate) => {
						return matcher(
							searchText,
							situation.canonicalName || ""
						);
					}
				);
				if (props.activeTypeIds.length) {
					const newIndex = newList.findIndex(
						(situation: SituationIntermediate) => {
							return (
								situation.uuid.uuidString ===
								props.activeTypeIds[0].uuidString
							);
						}
					);
					setFocusedReferent(newIndex === -1 ? undefined : newIndex);
				}
				setSituationList(newList);
			}
		} else {
			if (mentionModel === "entity") {
				setEntityList(props.entityList);
			} else {
				setSituationList(props.situationList);
			}
		}
	}, [searchText, searchOpen, props.entityList, props.situationList]);

	// If metadata modal is opened, report to top level components
	useEffect(() => {
		setModalOpen(metaModalExpanded);
	}, [metaModalExpanded]);

	useEffect(() => {
		const keyDownHandler = (e: KeyboardEvent) => {
			if (
				e.metaKey &&
				e.key === "/" &&
				props.annotationScheme.subkind === "situation"
			) {
				toggleFunction();
			}
		};

		document.addEventListener("keydown", keyDownHandler);

		return () => document.removeEventListener("keydown", keyDownHandler);
	});

	/**
	 * sets argumentOntology in situationOntology or entityOntology in order of preference when in entity mode;
	 * sets situation ontology when in situation mode
	 * @returns
	 */
	function _initializeMetaOntology(): Ontology<Type> | undefined {
		if (mentionModel === "entity") {
			return props.annotationScheme.subkind === "entity"
				? props.annotationScheme.ontology
				: props.annotationScheme.ontology.argumentOntology;
		} else {
			return props.annotationScheme.ontology;
		}
	}

	/**
	 * Initializes a HashMap that stores the UUID of an entity as key and
	 * it's canonicalName as value
	 *
	 * @returns HashMap filled with entity set loaded from concrete file
	 * filtered by metadata
	 */
	function _initializeEntityMap(): Map<string, string> {
		const entityMap = new Map<string, string>();

		entityList.forEach((entity, idx) => {
			if (entity.canonicalName !== undefined)
				entityMap.set(entity.uuid.uuidString, entity.canonicalName);
		});

		return entityMap;
	}

	/**
	 * Initializes a HashMap that stores the UUID of an situation as key and
	 * it's canonicalName as value
	 *
	 * @returns HashMap filled with situation set loaded from concrete file
	 * filtered by metadata
	 */
	function _initializeSituationMap(): Map<string, string> {
		const situationMap = new Map<string, string>();

		situationList.forEach((situation, idx) => {
			if (situation.canonicalName !== undefined)
				situationMap.set(
					situation.uuid.uuidString,
					situation.canonicalName
				);
		});

		return situationMap;
	}

	/**
	 * Initializes a HashMap that stores the entity uuid as value and a set of
	 * situation_uuid-role_uuid string which it has been a part of
	 *
	 * @returns HashMap with the configuration preloaded from the config file
	 * filtered by metadata
	 *
	 */
	function _initializeEntityToSituationRoleMap(): Map<string, Set<string>> {
		const map = new Map<string, Set<string>>();

		situationList.forEach((situation) => {
			situation.argumentList?.forEach((role) => {
				role.entityId?.forEach((entityId) => {
					if (!map.has(entityId)) {
						map.set(entityId, new Set<string>());
					}
					map.get(entityId)?.add(
						situation.uuid.uuidString + "-" + role.role
					);
				});
			});
		});
		return map;
	}

	function initializeSituationArgumentMap(
		situationList: SituationIntermediate[]
	): Map<string, Map<string, Set<string>>> {
		const argumentMap = new Map<string, Map<string, Set<string>>>();
	
		situationList.forEach((situation, index) => {
			if (!argumentMap.has(situation.uuid.uuidString))
				argumentMap.set(
					situation.uuid.uuidString,
					new Map<string, Set<string>>()
				);
			const situationAddress = argumentMap.get(situation.uuid.uuidString);
			situation.argumentList?.forEach((arg, idx) => {
				if (arg.role) {
					if (arg.entityId) {
						situationAddress?.set(arg.role, new Set(arg.entityId));
					} else if (arg.situationId) {
						situationAddress?.set(arg.role, new Set(arg.situationId));
					}
				}
			});
		});
	
		return argumentMap;
	}

	/**
	 * Adds a referent to either entityList or situationList with mentionIdList initialized to the
	 * currently active spans (props.activeStructureIds)
	 */
	const createReferentWithMention = (inputText?: string) => {
		const text =
			(props.activeStructureIds.length
				? props.spanMap.get(props.activeStructureIds[0][0]?.uuidString)
				: inputText) || "Untitled";
		if (mentionModel === "entity") {
			const entity: Entity = {
				uuid: {
					uuidString: uuidv4(),
				},
				mentionIdList: [
					...props.activeStructureIds.map(([uuid1]) => uuid1),
				],
				canonicalName: text,
			};
			props.createEntity(entity);
			entityMap.current.set(
				entity.uuid.uuidString,
				entity.canonicalName!
			);
			const message = props.activeStructureIds.length
				? `'${props.spanMap.get(
						props.activeStructureIds[0][0].uuidString
				  )}' added to entity '${entity.canonicalName}'`
				: `Created entity '${entity.canonicalName}'`;
			props.displayToast(message, "success");
			props.activeStructureIds.length
				? props.addToUserInteractions(
						"add",
						{
							type: "entityMention",
							text: props.activeStructureIds[0][0].uuidString,
						},
						undefined,
						undefined,
						undefined,
						entity.canonicalName
				  )
				: props.addToUserInteractions("create", {
						type: "entity",
						text: entity.canonicalName || "",
				  });
		} else {
			const situation: SituationIntermediate = {
				uuid: {
					uuidString: uuidv4(),
				},
				mentionIdList: [
					...props.activeStructureIds.map(([uuid1]) => uuid1),
				],
				canonicalName: text,
				situationType: "",
			};
			props.createSituation(situation);
			situationMap.current.set(
				situation.uuid.uuidString,
				situation.canonicalName!
			);
			const message = props.activeStructureIds.length
				? `'${props.spanMap.get(
						props.activeStructureIds[0][0].uuidString
				  )}' added to situation '${situation.canonicalName}'`
				: `Created situation '${situation.canonicalName}'`;
			props.displayToast(message, "success");
			props.activeStructureIds.length
				? props.addToUserInteractions(
						"add",
						{
							type: "situationMention",
							text: props.activeStructureIds[0][0].uuidString,
						},
						undefined,
						undefined,
						undefined,
						situation.canonicalName
				  )
				: props.addToUserInteractions("create", {
						type: "situation",
						text: situation.canonicalName || "",
				  });
		}
		setUntitledReferent(false);
		const index =
			mentionModel === "entity"
				? entityList.length
				: situationList.length;
		setFocusedReferent(index);
	};

	/**
	 * Deletes entity from entityList
	 * @param uuid
	 * UUID of the entity to be deleted
	 */
	const deleteEntity = (uuid: UUID): void => {
		const message = `Deleted entity '${entityMap.current.get(
			uuid.uuidString
		)}' and removed from all role-fillers(if any).`;
		props.addToUserInteractions("delete", {
			type: "entity",
			text: entityMap.current.get(uuid.uuidString) || "",
		});
		props.displayToast(message, "success");
		props.deleteEntity(uuid);
		entityMap.current.delete(uuid.uuidString);
	};

	/**
	 * Deletes situation from entityList
	 * @param uuid
	 * UUID of the situation to be deleted
	 */
	const deleteSituation = (uuid: UUID): void => {
		const message = `Deleted situation '${situationMap.current.get(
			uuid.uuidString
		)}'`;
		props.addToUserInteractions("delete", {
			type: "situation",
			text: situationMap.current.get(uuid.uuidString) || "",
		});
		props.displayToast(message, "success");
		props.deleteSituation(uuid);
		situationMap.current.delete(uuid.uuidString);
	};

	/**
	 * Sets various properties of an entity
	 * @param attribute
	 * The string name of the property being updated or added
	 * @param value
	 * The value to set the attribute to
	 */
	const mutateEntity = (
		attribute: string,
		value: string
	): void => {
		if (
			focusedReferent !== undefined &&
			(attribute === "canonicalName" || attribute === "type") &&
			entityList[focusedReferent][attribute] !== value
		) {
			const message = value
				? `Entity '${entityList[focusedReferent].canonicalName}' ${attribute} set to '${value}'`
				: `Cleared ${entityList[focusedReferent].canonicalName}' ${attribute}`;
			props.displayToast(message, "success");
			props.addToUserInteractions(
				"set",
				{
					type: "entity",
					text: entityList[focusedReferent].canonicalName || "",
				},
				{
					type: attribute,
					value: value,
				}
			);
			if (attribute === "canonicalName") {
				entityMap.current.set(
					entityList[focusedReferent].uuid.uuidString,
					value
				);
			}
			props.mutateEntity(
				attribute,
				value,
				entityList[focusedReferent].uuid.uuidString
			);
		}
	};

	/**
	 * Sets various properties of a situation
	 * @param attribute
	 * The string name of the property being updated or added
	 * @param value
	 * The value to set the attribute to
	 */
	const mutateSituation = (
		attribute: string,
		value: string,
		kind?: "entity" | "situation" | "options"
	): void => {
		if (focusedReferent !== undefined) {
			const uuid = situationList[focusedReferent].uuid.uuidString;
			const map = new Map(referentSituationArgumentMap);

			let propFunctionFlag = false;

			if (
				attribute === "canonicalName" &&
				situationList[focusedReferent].canonicalName !== value
			) {
				const message = value
					? `Situation '${situationList[focusedReferent].canonicalName}' ${attribute} set to '${value}'`
					: `Cleared ${situationList[focusedReferent].canonicalName}' ${attribute}`;
				props.displayToast(message, "success");
				props.addToUserInteractions(
					"set",
					{
						type: "situation",
						text:
							situationList[focusedReferent].canonicalName || "",
					},
					{
						type: attribute,
						value: value,
					}
				);

				situationMap.current.set(
					situationList[focusedReferent].uuid.uuidString,
					value
				);
				propFunctionFlag = true;
			} else if (
				attribute === "situationType" &&
				situationList[focusedReferent].situationType !== value
			) {
				const message = value
					? `Situation '${situationList[focusedReferent].canonicalName}' ${attribute} set to '${value}'`
					: `Cleared ${situationList[focusedReferent].canonicalName}' ${attribute}`;
				props.displayToast(message, "success");
				props.addToUserInteractions(
					"set",
					{
						type: "situation",
						text:
							situationList[focusedReferent].canonicalName || "",
					},
					{
						type: attribute,
						value: value,
					}
				);
				map.get(uuid)?.forEach((entities, role) => {
					const situationRoleIdentifier = uuid + "-" + role;
					entities.forEach((entity) => {
						entityToSituationRoleMap.current
							.get(entity)
							?.delete(situationRoleIdentifier);
					});
				});
				map.delete(uuid);
				setReferentSituationArgumentMap(map);
				propFunctionFlag = true;
			} else if (
				attribute !== "canonicalName" &&
				attribute !== "situationType"
			) {
				if (!map.has(uuid)) {
					map.set(uuid, new Map<string, Set<string>>());
				}
				if (!map.get(uuid)?.has(attribute)) {
					map.get(uuid)?.set(attribute, new Set<string>());
				}
				if (!map.get(uuid)?.get(attribute)?.has(value)) {
					map.get(uuid)?.get(attribute)?.add(value);

					const message = `Added '${
						entityMap.current.get(value) ||
						situationMap.current.get(value) ||
						value
					}' to '${
						situationList[focusedReferent].canonicalName
					}' role '${attribute}'`;
					if (
						entityMap.current.has(value) &&
						!entityToSituationRoleMap.current.has(value)
					) {
						entityToSituationRoleMap.current.set(
							value,
							new Set<string>()
						);
					}
					entityToSituationRoleMap.current
						.get(value)
						?.add(
							situationList[focusedReferent].uuid.uuidString +
								"-" +
								attribute
						);
					props.displayToast(message, "success");
					props.addToUserInteractions(
						"add",
						{
							type: "entity",
							text: entityMap.current.get(value) || "",
						},
						undefined,
						undefined,
						attribute,
						situationList[focusedReferent].canonicalName
					);
					setReferentSituationArgumentMap(map);
				}
				propFunctionFlag = true;
			}

			if (propFunctionFlag)
				props.mutateSituation(
					attribute,
					value,
					situationList[focusedReferent].uuid.uuidString,
					kind
				);
		}
	};

	/**
	 * Helper function to change currently focused index (focusedReferent)
	 *
	 * @param index
	 * numerical value indicates index of entity in entityList to be focused, -1 indicates last entity
	 * 'up' refers to decrementing index by 1, 'down' referes to incrementing index by 1
	 */
	const selectReferentByIndex = (index?: number | "up" | "down"): void => {
		if (index === undefined) {
			setFocusedReferent(undefined);
		}
		if (mentionModel === "entity") {
			if (entityList.length !== 0) {
				if (index === -1) {
					setFocusedReferent(entityList.length - 1);
				} else if (
					typeof index === "number" &&
					index < entityList.length &&
					index >= 0
				) {
					setFocusedReferent(index);
				} else if (index === "up") {
					setFocusedReferent((i) => {
						if (i !== undefined && i === 0) {
							return entityList.length - 1;
						} else if (i !== undefined) {
							return i - 1;
						}
					});
				} else if (index === "down") {
					setFocusedReferent((i) => {
						if (i !== undefined && i === entityList.length - 1) {
							return 0;
						} else if (i !== undefined) {
							return i + 1;
						}
					});
				}
			}
		} else {
			if (situationList.length !== 0) {
				if (index === -1) {
					setFocusedReferent(situationList.length - 1);
				} else if (
					typeof index === "number" &&
					index < situationList.length &&
					index >= 0
				) {
					setFocusedReferent(index);
				} else if (index === "up") {
					setFocusedReferent((i) => {
						if (i !== undefined && i === 0) {
							return situationList.length - 1;
						} else if (i !== undefined) {
							return i - 1;
						}
					});
				} else if (index === "down") {
					setFocusedReferent((i) => {
						if (i !== undefined && i === situationList.length - 1) {
							return 0;
						} else if (i !== undefined) {
							return i + 1;
						}
					});
				}
			}
		}
	};

	/**
	 * Combines callbacks to add mention to a referent (either Entity or Situation)
	 * @param referent
	 * The referent object to add the mention to (either an Entity or a Situation)
	 * @param mentionId
	 * The UUID of the mention being added to the referent's mentionIdList
	 */
	const addMentionToReferent = (
		referent: Entity | SituationIntermediate,
		mentionId: UUID[]
	) => {
		if (mentionModel === "entity") {
			props.addMentionToEntity(referent as Entity, mentionId);
		} else {
			props.addMentionToSituation(
				referent as SituationIntermediate,
				mentionId
			);
		}
	};

	/**
	 * Combines callbacks to remove a mention from a refernt (either Entity or Situation)
	 * @param referent
	 * The referent object to remove the mention from (either an Entity or a Situation)
	 * @param mentionId
	 * The UUID of the mention being removed from the referent's mentionIdList
	 */
	const removeMentionFromReferent = (
		referent: Entity | SituationIntermediate,
		mentionId: UUID
	) => {
		if (mentionModel === "entity") {
			props.removeMentionFromEntity(referent as Entity, mentionId);
		} else {
			props.removeMentionFromSituation(
				referent as SituationIntermediate,
				mentionId
			);
		}
	};

	const removeReferentFromRole = (
		situationId: string,
		role: string,
		referentId: string
	) => {
		const message = `Removed '${
			entityMap.current.get(referentId) ||
			situationMap.current.get(referentId || referentId)
		}' situation  '${situationMap.current.get(
			situationId
		)}' role '${role}'`;
		props.displayToast(message, "success");
		props.addToUserInteractions(
			"delete",
			{
				type: "entity",
				text: entityMap.current.get(referentId) || "",
			},
			undefined,
			undefined,
			role,
			situationMap.current.get(situationId)
		);
		const map = new Map(referentSituationArgumentMap);
		map.get(situationId)?.get(role)?.delete(referentId);
		setReferentSituationArgumentMap(map);

		entityToSituationRoleMap.current
			.get(referentId)
			?.delete(situationId + "-" + role);
	};

	let elements;
	let metaContent = (
		<div className="meta-empty-info">
			{mentionModel === "entity"
				? "Select entity to show metadata"
				: "Select situation to show metadata"}
		</div>
	);
	let metaModal = null;

	// Set the value of elements, renders a list of ReferentialEntry elements
	if (mentionModel === "entity") {
		elements = entityList.map((entity: Entity, index: number) => {
			return (
				<Referent
					referent={entity}
					index={index}
					focusedReferent={focusedReferent}
					selectReferentByIndex={selectReferentByIndex}
					activeStructureIds={props.activeStructureIds}
					activateStructureIds={props.activateStructureIds}
					deleteReferent={deleteEntity}
					addMentionToReferent={addMentionToReferent}
					removeMentionFromReferent={removeMentionFromReferent}
					key={"entity-entry-" + entity.uuid.uuidString}
					spanMap={props.spanMap}
					modalExpanded={metaModalExpanded}
					currentMentionModel={mentionModel}
				/>
			);
		});
	} else {
		elements = situationList.map(
			(situation: SituationIntermediate, index: number) => {
				return (
					<Referent
						referent={situation}
						index={index}
						focusedReferent={focusedReferent}
						selectReferentByIndex={selectReferentByIndex}
						activeStructureIds={props.activeStructureIds}
						activateStructureIds={props.activateStructureIds}
						deleteReferent={deleteSituation}
						addMentionToReferent={addMentionToReferent}
						removeMentionFromReferent={removeMentionFromReferent}
						key={"situation-entry-" + situation.uuid.uuidString}
						spanMap={props.spanMap}
						modalExpanded={metaModalExpanded}
						currentMentionModel={mentionModel}
					/>
				);
			}
		);
	}

	const buttonClickHandler = () => {
		if (
			props.activeStructureIds.length ||
			document.querySelector("#hiddenSubmit")
		) {
			createReferentWithMention();
		} else {
			setUntitledReferent(true);
		}
	};

	const button = (
		<div
			className={"add-referent " + mentionModel}
			onClick={buttonClickHandler}>
			+
		</div>
	);

	const toggleFunction = () => {
		props.setMentionModel((model) => {
			if (model === "entity") {
				props.displayToast("Switched to SITUATION mode", "info");
				return "situation";
			}
			props.displayToast("Switched to ENTITY mode", "info");
			return "entity";
		});

		setMetaOntology(() => {
			if (mentionModel === "entity") {
				return props.annotationScheme.ontology;
			} else {
				return props.annotationScheme.subkind === "entity"
					? props.annotationScheme.ontology
					: props.annotationScheme.ontology.argumentOntology;
			}
		});

		props.activateStructureIds([], [], mentionModel);
	};

	const toggleHeader =
		props.annotationScheme.subkind === "situation" ? (
			<ToggleButton
				toggleFunction={toggleFunction}
				disabled={false}
				label={mentionModel === "entity" ? "Entity" : "Situation"}
				checked={mentionModel === "situation"}
			/>
		) : (
			<h1>Entity</h1>
		);

	const searchBar = searchOpen ? (
		<SearchBar
			searchText={searchText}
			setSearchText={setSearchText}
			setOpen={setSearchOpen}
			mentionModel={mentionModel}
			deactivateStrutureIds={props.deactivateStructureIds}
		/>
	) : null;

	if (focusedReferent !== undefined) {
		if (mentionModel === "entity" && entityList.length) {
			const disabled =
				entityToSituationRoleMap.current.has(
					entityList[focusedReferent]?.uuid.uuidString
				) &&
				entityToSituationRoleMap.current.get(
					entityList[focusedReferent]?.uuid.uuidString
				)!.size > 0;
			metaContent = (
				<>
					<div className={"header " + mentionModel}>Entity</div>
					<MetaItem
						label="Name"
						value={entityList[focusedReferent]?.canonicalName || ""}
						mutateReferent={mutateEntity}
						attribute={"canonicalName"}
						map={entityMap.current}
						disabled={false}
					/>
					{metaOntology ? (<MetaItem
						label="Type"
						value={entityList[focusedReferent]?.type || ""}
						mutateReferent={mutateEntity}
						attribute={"type"}
						dropDown={metaOntology}
						dropDownType={"ontology"}
						map={entityMap.current}
						disabled={disabled}
						disabledMessageShow={props.displayToast}
					/>) : null}
				</>
			);
		} else if (mentionModel === "situation" && situationList.length) {
			const metaItems: JSX.Element[] = [];

			if (
				focusedReferent < situationList.length &&
				situationList[focusedReferent].situationType != undefined
			) {
				let situationRoles = undefined;

				const disableMap = new Map<string, boolean>();

				if (props.annotationScheme.subkind === "situation") {
					if (
						props.annotationScheme.roleOntologyType === "particular"
					) {
						situationRoles = roleSetMap.current.get(
							situationList[focusedReferent].situationType
						);
					} else if (
						props.annotationScheme.roleOntologyType === "general"
					) {
						situationRoles = roleSetMap.current.get("types");
					}
				}

				// Using role.value instead of UUID because concrete stores role.value
				situationRoles?.forEach((role) => {
					disableMap.set(role.value, false);
				});

				const rootNode =
					props.annotationScheme.subkind === "situation" &&
					props.annotationScheme.roleOntologyType === "particular"
						? situationExpressionTree.current.get(
								situationList[focusedReferent].situationType
						  )
						: situationExpressionTree.current.get("situation");

				if (rootNode)
					referentTypeCheck(
						rootNode,
						situationList[focusedReferent].uuid.uuidString,
						referentSituationArgumentMap,
						disableMap,
						false
					);

				situationRoles?.forEach((role) => {
					let dropdownOptions, map;
					let kind: "situation" | "entity" | "options" | "string";

					const uuid = situationList[focusedReferent].uuid.uuidString;
					const referentIds = referentSituationArgumentMap
						.get(uuid)
						?.get(role.value);
					let values: JSX.Element[] = [];

					referentIds?.forEach((value) => {
						const text =
							entityMap.current.get(value) ||
							situationMap.current.get(value);
						if (text === undefined) {
							removeReferentFromRole(
								situationList[focusedReferent].uuid.uuidString,
								role.value,
								value
							);
						} else {
							const button = (
								<Box
									text={text}
									uuid={value}
									role={role.value}
									parentSituation={
										situationList[focusedReferent].uuid
											.uuidString
									}
									removeReferent={removeReferentFromRole}
								/>
							);

							values.push(button);
						}
					});

					const currentRole = role as RoleType;
					switch (currentRole.argumentTypes?.kind) {
						case "entity":
							dropdownOptions = props.entityList.filter(
								(entity) =>
									currentRole.argumentTypes === undefined ||
									currentRole.argumentTypes.value ===
										entity.type
							);
							map = entityMap.current;
							kind = "entity";
							break;
						case "situation":
							dropdownOptions = props.situationList;
							map = situationMap.current;
							kind = "situation";
							break;
						default:
							dropdownOptions = props.entityList.filter(
								(entity) =>
									currentRole.argumentTypes === undefined ||
									currentRole.argumentTypes.value ===
										entity.type
							);
							map = entityMap.current;
							kind = "entity";
							break;
					}

					const roleValues = (
						<div className="values-referent">{values}</div>
					);

					const item = (
						<MetaItem
							label={role.value}
							value={""}
							dropDown={dropdownOptions}
							dropDownType={kind}
							mutateReferent={mutateSituation}
							attribute={role.value}
							map={map}
							roleValues={roleValues}
							disabled={disableMap.get(role.value) || false}
							placeHolderEntity={currentRole.argumentTypes?.value}
						/>
					);

					metaItems.push(item);
				});
			}
			metaContent = (
				<>
					<div className="header situation">
						Situation
						<div
							className="expand"
							onClick={() => {
								setMetaModalExpanded(true);
							}}
							title="Click here to modify roles">
							{expandSVG}
						</div>
					</div>
					<MetaItem
						label="Name"
						value={
							situationList[focusedReferent]?.canonicalName || ""
						}
						mutateReferent={mutateSituation}
						attribute={"canonicalName"}
						map={situationMap.current}
						disabled={false}
					/>
					<MetaItem
						label="Situation Type"
						value={
							situationList[focusedReferent]?.situationType || ""
						}
						mutateReferent={mutateSituation}
						attribute={"situationType"}
						map={situationMap.current}
						dropDown={metaOntology}
						dropDownType={"ontology"}
						disabled={false}
					/>
				</>
			);
			metaModal = (
				<Modal
					key={situationList[focusedReferent]?.uuid.uuidString || ""}
					content={metaItems}
					open={metaModalExpanded}
					handleClose={() => {
						setMetaModalExpanded(false);
					}}
					emptyInfo={
						situationList[focusedReferent]?.situationType.length > 0
							? metaItems.length
								? undefined
								: "Situation doesn't belong to this ontology, please select a different Situation Type"
							: "Select Situation type to display roles"
					}
					header={"Metadata"}
					mentionModel={mentionModel}
				/>
			);
		}
	}

	return (
		<div className="menu">
			{searchBar}
			<div className={"header " + mentionModel}>
				{toggleHeader}
				{button}
			</div>
			<div className="content">
				{elements}
				{untitledReferent ? (
					<UntitledReferent
						createReferent={createReferentWithMention}
						selectReferent={selectReferentByIndex}
					/>
				) : null}
			</div>
			<div
				className="metadata-referential"
				onClick={(e: React.MouseEvent) => {
					e.stopPropagation();
				}}>
				{/* TODO: Hardcoded list, make dynamic / review  */}
				<div className="content">
					{metaContent}
					{metaModal}
				</div>
			</div>
		</div>
	);
}

export default ReferentAnnotation;
